import { dictionaryApi } from './dictionaryClient';
import type { SubantaLinga } from './subantaApi';

export type GanaSubantaSummary = {
  id: number;
  word: string;
  iast: string;
  linga?: SubantaLinga;
};

export type GanaAssociation = {
  subantaId: number;
  subanta: GanaSubantaSummary | null;
};

export type Gana = {
  id: number;
  name: string;
  associations: GanaAssociation[];
};

export type CreateGanaBody = {
  name: string;
  associations: number[];
};

export type UpdateGanaBody = Partial<CreateGanaBody>;

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function toNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function toStr(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function normalizeNestedSubanta(raw: unknown): GanaSubantaSummary | null {
  if (!isRecord(raw)) return null;
  const id = toNum(raw.id);
  if (id == null) return null;
  const lingaRaw = toStr(raw.linga);
  return {
    id,
    word: toStr(raw.word),
    iast: toStr(raw.iast),
    linga: lingaRaw ? (lingaRaw as SubantaLinga) : undefined,
  };
}

export function normalizeGanaAssociation(raw: unknown): GanaAssociation | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return { subantaId: raw, subanta: null };
  }
  if (!isRecord(raw)) return null;
  const subantaId = toNum(raw.subantaId ?? raw.subanta_id);
  if (subantaId == null) return null;
  const subantaRaw = raw.subanta;
  const subanta = subantaRaw ? normalizeNestedSubanta(subantaRaw) : null;
  return { subantaId, subanta };
}

export function normalizeGana(raw: unknown): Gana | null {
  if (!isRecord(raw)) return null;
  const id = toNum(raw.id);
  if (id == null) return null;
  const assocRaw = raw.associations;
  const associations = Array.isArray(assocRaw)
    ? assocRaw.map(normalizeGanaAssociation).filter((x): x is GanaAssociation => x != null)
    : [];
  return {
    id,
    name: toStr(raw.name),
    associations,
  };
}

function normalizeList(data: unknown): Gana[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows.map(normalizeGana).filter((x): x is Gana => x != null);
}

export function associationIds(associations: GanaAssociation[]): number[] {
  return associations.map((a) => a.subantaId);
}

export function buildGanaPatch(before: Gana, after: { name: string; associations: number[] }): UpdateGanaBody {
  const patch: UpdateGanaBody = {};
  if (after.name !== before.name) patch.name = after.name;
  const prev = [...associationIds(before.associations)].sort((a, b) => a - b);
  const next = [...after.associations].sort((a, b) => a - b);
  if (JSON.stringify(prev) !== JSON.stringify(next)) {
    patch.associations = after.associations;
  }
  return patch;
}

export async function listGana(): Promise<Gana[]> {
  const res = await dictionaryApi.get<unknown>('/gana');
  return normalizeList(res.data);
}

export async function getGana(id: number): Promise<Gana> {
  const res = await dictionaryApi.get<unknown>(`/gana/${id}`);
  const row = normalizeGana(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid gana response');
  return row;
}

export async function createGana(body: CreateGanaBody): Promise<Gana> {
  const res = await dictionaryApi.post<unknown>('/gana', body);
  const row = normalizeGana(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid gana response');
  return row;
}

export async function updateGana(id: number, body: UpdateGanaBody): Promise<Gana> {
  const res = await dictionaryApi.patch<unknown>(`/gana/${id}`, body);
  const row = normalizeGana(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid gana response');
  return row;
}

export async function deleteGana(id: number): Promise<void> {
  await dictionaryApi.delete(`/gana/${id}`);
}
